import { types, getSnapshot, applySnapshot, getRoot } from 'mobx-state-tree';

export const History = types
  .model('History', {
    history: types.optional(types.array(types.frozen()), []),
    undoIdx: types.optional(types.number, -1),
    targetPath: types.optional(types.string, ''),
  })
  .volatile(() => ({
    _isTransacting: false,
    _skipSave: false,
    _ignoring: false,
  }))
  .views((self) => ({
    get canUndo() {
      return self.undoIdx > 0;
    },
    get canRedo() {
      return self.undoIdx < self.history.length - 1;
    },
  }))
  .actions((self) => {
    let disposer = null;

    return {
      startTransaction() {
        self._isTransacting = true;
      },
      endTransaction(skipSave = false) {
        self._isTransacting = false;
        if (!skipSave) {
          self.addUndoState();
        }
      },
      ignore(func) {
        self._ignoring = true;
        try {
          func();
        } finally {
          self._ignoring = false;
        }
      },
      async transaction(func) {
        self.startTransaction();
        try {
          await func();
        } finally {
          self.endTransaction();
        }
      },
      addUndoState() {
        if (self._ignoring) return;
        try {
          const root = getRoot(self);
          const snapshot = getSnapshot(root);
          // Strip history (circular) and zoom-level properties (UI preference,
          // not content — should not be affected by undo/redo).
          const stateToSave = { ...snapshot };
          delete stateToSave.history;
          delete stateToSave.scale;
          delete stateToSave.scaleToFit;

          // Remove future states if we're in the middle of history
          if (self.undoIdx < self.history.length - 1) {
            self.history.splice(self.undoIdx + 1);
          }

          self.history.push(stateToSave);
          self.undoIdx = self.history.length - 1;

          // Limit history size to 100 entries
          if (self.history.length > 100) {
            self.history.splice(0, self.history.length - 100);
            self.undoIdx = self.history.length - 1;
          }
        } catch (e) {
          console.warn('History: failed to save state', e);
        }
      },
      _restoreState(state) {
        if (!state) return;
        self._ignoring = true;
        try {
          const root = getRoot(self);
          const currentHistory = getSnapshot(self);

          // Preserve zoom level across undo/redo — scale and scaleToFit are UI
          // preferences, not content, so they are excluded from history snapshots.
          const currentScale = root.scale;
          const currentScaleToFit = root.scaleToFit;

          // Apply the full snapshot (children may get reordered by MST reconciliation)
          applySnapshot(root, {
            ...state,
            history: currentHistory,
            scale: currentScale,
            scaleToFit: currentScaleToFit,
          });

          // Fix children order — MST's identifier-based reconciliation can
          // reorder arrays. Use getSnapshot→clear→push (same pattern as
          // page-model moveElementUp/Down) to enforce the saved order.
          if (state.pages) {
            state.pages.forEach((savedPage) => {
              const page = root.pages.find((p) => p.id === savedPage.id);
              if (!page || !savedPage.children || !savedPage.children.length) return;
              const targetIds = savedPage.children.map((c) => c.id);
              const currentIds = page.children.map((c) => c.id);
              // Only reorder if order actually differs
              if (targetIds.length === currentIds.length &&
                  targetIds.every((id, i) => id === currentIds[i])) return;
              const snapById = {};
              page.children.forEach((c) => { snapById[c.id] = getSnapshot(c); });
              page.children.clear();
              targetIds.forEach((id) => {
                if (snapById[id]) page.children.push(snapById[id]);
              });
            });
          }
        } catch (e) {
          console.warn('History: failed to restore state', e);
        } finally {
          self._ignoring = false;
        }
      },
      undo() {
        if (!self.canUndo) return;
        self.undoIdx -= 1;
        self._restoreState(self.history[self.undoIdx]);
      },
      redo() {
        if (!self.canRedo) return;
        self.undoIdx += 1;
        self._restoreState(self.history[self.undoIdx]);
      },
      clear() {
        self.history.clear();
        self.undoIdx = -1;
      },
      afterCreate() {
        // Do NOT defer an addUndoState() here.
        //
        // The previous setTimeout(addUndoState, 0) fired asynchronously and
        // created a race condition with loadJSON:
        //
        //   • If the timeout fired BEFORE loadJSON: the empty-store snapshot
        //     was saved as history[0], then loadJSON's history.clear() removed
        //     it and saved the correct template as the new history[0] — OK so
        //     far, but then the timeout could also fire AFTER loadJSON and add
        //     a stale duplicate as history[1], making undoIdx=1 instead of 0.
        //
        //   • Either way, history[0] ended up being saved with whatever `scale`
        //     was current at a moment we did not control (potentially a negative
        //     value produced by the ResizeObserver when the container had
        //     width=0), so the first undo restored that wrong scale and mirrored
        //     the entire canvas.
        //
        // loadJSON (and store.clear) both call history.clear() + addUndoState()
        // explicitly, and now do so AFTER resetting scale=1, so they own the
        // "initial state" responsibility completely.
      },
      beforeDestroy() {
        if (disposer) {
          disposer();
          disposer = null;
        }
      },
      replaceState() {
        // Replace the current state in history (for non-undoable changes)
        if (self._ignoring) return;
        try {
          const root = getRoot(self);
          const snapshot = getSnapshot(root);
          const stateToSave = { ...snapshot };
          delete stateToSave.history;

          if (self.undoIdx >= 0 && self.undoIdx < self.history.length) {
            self.history[self.undoIdx] = stateToSave;
          }
        } catch (e) {
          console.warn('History: failed to replace state', e);
        }
      },
      requestAddState() {
        // Debounced version - just calls addUndoState
        self.addUndoState();
      },
    };
  });
